import { useEffect, useState } from 'react'

/**
 * Spec 03 · Breakpoints — three named stops, nothing else.
 * sm 560 (grid 1→2, stacked fields) · md 920 (nav collapses, panels
 * stack) · lg 1200 (grid 4-up, full chrome).
 */
export const BREAKPOINTS = { sm: 560, md: 920, lg: 1200 } as const

export function useMediaQuery(query: string): boolean {
  // This app mounts with createRoot. Read the viewport before its first
  // paint instead of briefly rendering desktop media on a phone.
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(query).matches
  )

  useEffect(() => {
    const mql = window.matchMedia(query)
    const update = () => setMatches(mql.matches)
    update()
    mql.addEventListener('change', update)
    return () => mql.removeEventListener('change', update)
  }, [query])

  return matches
}

/** True below the sm stop — the "mini" layout the mobile frames draw. */
export function useIsMini(): boolean {
  return useMediaQuery(`(width < ${BREAKPOINTS.sm}px)`)
}

/** True below the md stop — nav collapses, panels stack. */
export function useIsCompact(): boolean {
  return useMediaQuery(`(max-width: ${BREAKPOINTS.md - 1}px)`)
}

export function usePrefersReducedMotion(): boolean {
  return useMediaQuery('(prefers-reduced-motion: reduce)')
}
